"use client"

import { useEffect, useState } from "react"
import { API_ENDPOINTS, useApi } from "@/lib/api"
import { useAuth } from "@/contexts/auth-context"
import { parseDocEnvelopes, parseProviderConfigData, parseValueMappingSetData } from "@/lib/global-config"
import { onHubCountsInvalidated } from "@/lib/hub-counts-refresh"
import { GROUP_NAMES } from "@/types/payroll"

/** How many backfill candidates to ask for. Only the count is wanted, so a page is enough; `candidatesHasMore` says the real number is higher. */
const CANDIDATES_TAKE = 100

export interface HubCounts {
  /** Workers with a migration record. */
  workers: number | null
  /** The enabled provider's name; "" when none is set up. */
  providerName: string | null
  /** That provider's environment ("sandbox" / "production"), when one is set up. */
  providerEnvironment: string | null
  /** Value-translation lists that actually have rows. */
  valueSets: number | null
  /** Active workers not yet tracked. */
  candidates: number | null
  candidatesHasMore: boolean
}

const EMPTY: HubCounts = {
  workers: null,
  providerName: null,
  providerEnvironment: null,
  valueSets: null,
  candidates: null,
  candidatesHasMore: false,
}

/**
 * The figures on the hub's summary-card tabs.
 *
 * Each is null until it arrives and stays null if its call fails, so a card
 * shows "—" rather than a wrong zero — the tabs must never imply there is
 * nothing to see when the truth is that we could not ask. Every call is
 * independent: one failure never blanks the others.
 *
 * These duplicate reads the tab pages make for themselves. That is deliberate:
 * the cards have to be right before a tab is ever opened, and each page owns
 * its own refresh and filter state.
 */
export function useHubCounts(): HubCounts {
  const { token } = useAuth()
  const { adminApi, payrollApi } = useApi()
  const [counts, setCounts] = useState<HubCounts>(EMPTY)
  // Bumped when a tab saves something a card counts, so the effect refetches.
  const [version, setVersion] = useState(0)

  useEffect(() => onHubCountsInvalidated(() => setVersion((v) => v + 1)), [])

  useEffect(() => {
    if (!token) return
    let cancelled = false
    const set = (patch: Partial<HubCounts>) => {
      if (!cancelled) setCounts((c) => ({ ...c, ...patch }))
    }

    void payrollApi
      .get<{ counts: Record<string, number> }>(API_ENDPOINTS.PAYROLL.MIGRATION_STATUS_COUNTS)
      .then((r) => set({ workers: Object.values(r.counts ?? {}).reduce((sum, n) => sum + n, 0) }))
      .catch(() => set({ workers: null }))

    void adminApi
      .get<unknown>(API_ENDPOINTS.ADMIN.CONFIG_BY_GROUP(GROUP_NAMES.providerConfiguration))
      .then((raw) => {
        const configs = parseDocEnvelopes(raw).map((d) => parseProviderConfigData(d.data, ""))
        // The tenant's live provider, else the first one set up at all.
        const active = configs.find((c) => c.isEnabled) ?? configs[0]
        set({
          providerName: active?.provider ?? "",
          providerEnvironment: active?.providerEnvironmentName ?? null,
        })
      })
      .catch(() => set({ providerName: null, providerEnvironment: null }))

    void adminApi
      .get<unknown>(API_ENDPOINTS.ADMIN.CONFIG_BY_GROUP(GROUP_NAMES.valueMappingSet))
      .then((raw) => {
        // An empty document is a list an operator cleared — the mapper treats it
        // as absent, so it is not a configured translation either.
        const withRows = parseDocEnvelopes(raw).filter((d) => parseValueMappingSetData(d.data).mappings.length > 0)
        set({ valueSets: withRows.length })
      })
      .catch(() => set({ valueSets: null }))

    void payrollApi
      .get<{ data: unknown[]; hasMore?: boolean }>(`${API_ENDPOINTS.PAYROLL.BACKFILL_CANDIDATES}?take=${CANDIDATES_TAKE}`)
      .then((r) => set({ candidates: (r.data ?? []).length, candidatesHasMore: r.hasMore === true }))
      .catch(() => set({ candidates: null, candidatesHasMore: false }))

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, version])

  return counts
}
